/**
 * Position Timeline view — Faz 8.2 (ADR-062).
 *
 * Visualises the lifecycle of a single position (per-symbol FSM
 * transitions) as a vertical timeline grouped by episode segments.
 * Each row shows: tickId · prev → next FSM transition · event kind
 * · correlationId link · payload drill-down (`<details>`).
 *
 * URL-addressable: `/positions/:symbol[?fromTickId&toTickId]`. Symbol
 * input + range form on top so investigators can pivot from a specific
 * symbol without leaving the page. Links from `EventDetailView` /
 * `LineageGraphView` carry the symbol pre-filled.
 *
 * Reuses `JsonTree` for payload rendering (deterministic key sort +
 * BigInt-as-string display) and the `LineageGraphView` vertical
 * `border-l-2` chrome for visual continuity across views.
 */

import { useMemo, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "../api/client.js";
import { fetchPositionTimeline, queryKeys } from "../api/queries.js";
import { JsonTree } from "../components/JsonTree.js";
import type {
  PositionEpisodeWire,
  PositionTimelineEventWire,
} from "../api/types.js";

// =============================================================================
// State badge palette (12-string FSM × terminal closing-* family)
// =============================================================================

const stateBadgeColor = (s: string): string => {
  if (s === "idle") return "bg-slate-700 text-slate-300";
  if (s === "entry-pending") return "bg-sky-500/20 text-sky-300";
  if (s === "entry-filled-bracket-pending") return "bg-indigo-500/20 text-indigo-300";
  if (s === "open-pre-tat" || s === "open-tat-active")
    return "bg-emerald-500/20 text-emerald-300";
  if (s === "scale-in-phase2-pending" || s === "scale-in-phase2-filled")
    return "bg-violet-500/20 text-violet-300";
  if (s.startsWith("closing-") || s === "closed")
    return "bg-rose-500/20 text-rose-300";
  return "bg-amber-500/20 text-amber-300";
};

// =============================================================================
// View
// =============================================================================

export const PositionTimelineView = (): ReactElement => {
  const params = useParams<{ symbol: string }>();
  const symbolFromRoute = params.symbol ?? "";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTickId = searchParams.get("fromTickId") ?? "";
  const toTickId = searchParams.get("toTickId") ?? "";

  const [symbolInput, setSymbolInput] = useState(symbolFromRoute);
  const [fromInput, setFromInput] = useState(fromTickId);
  const [toInput, setToInput] = useState(toTickId);

  const query = useQuery({
    queryKey: queryKeys.positionTimeline(
      symbolFromRoute,
      fromTickId,
      toTickId,
    ),
    queryFn: () =>
      fetchPositionTimeline({
        symbol: symbolFromRoute,
        fromTickId: fromTickId === "" ? undefined : fromTickId,
        toTickId: toTickId === "" ? undefined : toTickId,
      }),
    enabled: symbolFromRoute !== "",
  });

  const onJump = (e: React.FormEvent): void => {
    e.preventDefault();
    if (symbolInput === "") return;
    const qs = new URLSearchParams();
    if (fromInput !== "") qs.set("fromTickId", fromInput);
    if (toInput !== "") qs.set("toTickId", toInput);
    const suffix = qs.toString();
    navigate(
      `/positions/${encodeURIComponent(symbolInput)}${suffix === "" ? "" : `?${suffix}`}`,
    );
  };

  const eventsByEpisode = useMemo(() => {
    const m = new Map<number, PositionTimelineEventWire[]>();
    for (const ev of query.data?.events ?? []) {
      const arr = m.get(ev.episodeIndex) ?? [];
      arr.push(ev);
      m.set(ev.episodeIndex, arr);
    }
    return m;
  }, [query.data]);

  return (
    <section className="space-y-4">
      <header className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">Position Timeline</h2>
        {symbolFromRoute !== "" && (
          <span className="rounded bg-slate-800 px-2 py-1 text-xs font-mono">
            {symbolFromRoute}
          </span>
        )}
      </header>

      <form
        onSubmit={onJump}
        className="flex flex-wrap items-end gap-3 rounded border border-slate-800 bg-slate-900/50 p-3"
        data-testid="jump-form"
      >
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Symbol
          <input
            value={symbolInput}
            onChange={(e) => setSymbolInput(e.target.value)}
            placeholder="BTCUSDT"
            className="w-32 rounded bg-slate-950 px-2 py-1 text-sm font-mono text-slate-100 outline-none ring-1 ring-slate-700 focus:ring-sky-500"
            data-testid="input-symbol"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          fromTickId
          <input
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
            placeholder="(optional)"
            className="w-40 rounded bg-slate-950 px-2 py-1 text-sm font-mono text-slate-100 outline-none ring-1 ring-slate-700 focus:ring-sky-500"
            data-testid="input-from"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          toTickId
          <input
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
            placeholder="(optional)"
            className="w-40 rounded bg-slate-950 px-2 py-1 text-sm font-mono text-slate-100 outline-none ring-1 ring-slate-700 focus:ring-sky-500"
            data-testid="input-to"
          />
        </label>
        <button
          type="submit"
          disabled={symbolInput === ""}
          className="rounded bg-sky-600 px-3 py-1 text-sm font-semibold text-slate-50 hover:bg-sky-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
          data-testid="jump-submit"
        >
          Load timeline
        </button>
      </form>

      {symbolFromRoute === "" && (
        <p className="text-sm text-slate-400" data-testid="empty-prompt">
          Enter a symbol above to load its position timeline.
        </p>
      )}

      {query.isLoading && symbolFromRoute !== "" && (
        <p data-testid="loading">Loading…</p>
      )}
      {query.isError && (
        <p className="text-rose-400" data-testid="error">
          {query.error instanceof ApiError
            ? `${query.error.server.kind}: ${query.error.server.message}`
            : "Unknown error"}
        </p>
      )}

      {query.data && (
        <>
          <p className="text-sm text-slate-400" data-testid="summary">
            {query.data.events.length} event(s) · {query.data.episodes.length}{" "}
            episode(s)
            {query.data.fromTickId !== null
              ? ` · from ${query.data.fromTickId}`
              : ""}
            {query.data.toTickId !== null
              ? ` · to ${query.data.toTickId}`
              : ""}
          </p>

          {query.data.episodes.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="no-episodes">
              No position episodes for this symbol in the queried range.
            </p>
          )}

          <div className="space-y-6">
            {query.data.episodes.map((ep) => (
              <EpisodeSection
                key={ep.index}
                episode={ep}
                events={eventsByEpisode.get(ep.index) ?? []}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

// =============================================================================
// Episode card + event row
// =============================================================================

interface EpisodeSectionProps {
  readonly episode: PositionEpisodeWire;
  readonly events: readonly PositionTimelineEventWire[];
}

const EpisodeSection = ({
  episode,
  events,
}: EpisodeSectionProps): ReactElement => (
  <article
    data-testid={`episode-${episode.index}`}
    className="rounded border border-slate-800 bg-slate-900/40 p-4"
  >
    <header className="mb-3 flex flex-wrap items-center gap-3">
      <span className="rounded bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-200">
        Episode #{episode.index}
      </span>
      <span className="font-mono text-xs text-slate-400">
        opened @ {episode.openingTickId} ({episode.openingKind})
      </span>
      {episode.closingTickId !== null ? (
        <span className="font-mono text-xs text-rose-300">
          closed @ {episode.closingTickId} ({episode.closingKind})
        </span>
      ) : (
        <span className="rounded bg-amber-500/20 px-2 py-0.5 text-xs font-semibold text-amber-300">
          open-ended
        </span>
      )}
      <span className="text-xs text-slate-500">
        {episode.eventCount} event(s)
      </span>
    </header>

    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-slate-500">
        State path:
      </span>
      {episode.statePath.map((s, i) => (
        <span key={`${i}-${s}`} className="flex items-center gap-1">
          <span
            className={`rounded px-2 py-0.5 text-xs font-mono ${stateBadgeColor(s)}`}
          >
            {s}
          </span>
          {i < episode.statePath.length - 1 && (
            <span className="text-slate-600">→</span>
          )}
        </span>
      ))}
    </div>

    {episode.correlationIds.length > 0 && (
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500">
          Correlations:
        </span>
        {episode.correlationIds.map((cid) => (
          <Link
            key={cid}
            to={`/lineage/${encodeURIComponent(cid)}`}
            className="rounded bg-slate-800 px-2 py-0.5 text-xs font-mono text-sky-400 hover:underline"
            data-testid={`correlation-link-${cid}`}
          >
            {cid}
          </Link>
        ))}
      </div>
    )}

    <ol className="space-y-1 border-l-2 border-slate-700 pl-4">
      {events.map((row) => (
        <EventRow key={String(row.event.tickId)} row={row} />
      ))}
    </ol>
  </article>
);

interface EventRowProps {
  readonly row: PositionTimelineEventWire;
}

const EventRow = ({ row }: EventRowProps): ReactElement => {
  const tickId = String(row.event.tickId);
  const cid = row.event.correlationId;
  return (
    <li
      data-testid={`timeline-row-${tickId}`}
      className="rounded px-2 py-1 hover:bg-slate-900/50"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-24 font-mono text-xs text-slate-400">{tickId}</span>
        <Link
          to={`/events/${tickId}`}
          className="font-mono text-sm text-sky-400 hover:underline"
        >
          {row.event.kind}
        </Link>
        <span
          className={`rounded px-2 py-0.5 text-xs font-mono ${stateBadgeColor(row.prevState.state)}`}
          data-testid={`prev-${tickId}`}
        >
          {row.prevState.state}
        </span>
        <span className="text-slate-500">→</span>
        <span
          className={`rounded px-2 py-0.5 text-xs font-mono ${stateBadgeColor(row.nextState.state)}`}
          data-testid={`next-${tickId}`}
        >
          {row.nextState.state}
        </span>
        {row.transitioned && (
          <span
            className="rounded bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-300"
            data-testid={`transition-${tickId}`}
          >
            transition
          </span>
        )}
        {cid !== "" && (
          <Link
            to={`/lineage/${encodeURIComponent(cid)}`}
            className="ml-auto font-mono text-xs text-sky-300 hover:underline"
          >
            {cid}
          </Link>
        )}
      </div>
      <details className="mt-1 ml-24 text-xs">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-300">
          payload
        </summary>
        <JsonTree value={row.event.payload} className="mt-1" />
      </details>
    </li>
  );
};
